/* eslint-disable no-param-reassign */

import { createSlice } from '@reduxjs/toolkit';
import { useSelector } from 'react-redux';
import listSaga from './list';
import byIdSaga from './byId';
import createSaga, { ReportCreateData } from './create';
import updateSaga, { ReportUpdateData } from './update';
import deleteSaga from './delete';
import { BaseState, createCRUDModel, createCRUDStates } from '../creator';
import { RootState } from '../index';
import { PaginatedResponse } from '../types';

export const moduleName = 'report';

export type ReportStatus = 'open' | 'in progress' | 'resolved';
export const ReportStatusTitle: Record<ReportStatus, string> = {
  open: 'Open',
  'in progress': 'In Progress',
  resolved: 'Resolved',
};

export type ReportType = 'comment' | 'business';
export const ReportTypeTitle: Record<ReportType, string> = {
  comment: 'Comment',
  business: 'Business',
};

export interface Report {
  id: string;
  link: string;
  reason: string;
  name: string;
  description: string;
  status: ReportStatus;
  type: ReportType;
  createdAt: string;
  updatedAt: string;
  comment: {
    id: string;
    text: string;
    numberOfLikes: number;
    numberOfDislikes: number;
    numberOfComments: number;
    numberOfCommentsTotal: number;
    createdAt: string;
    updatedAt: string;
    files: {
      id: string;
      originalName: string;
      location: string;
      mimeType: string;
      createdAt: string;
    }[];
    user: {
      id: string;
      email: string;
      name: string;
      phone: string;
      createdAt: string;
      zipCode: string;
      yearBirth: number;
      description: string;
    }
  }
  user: {
    id: string;
    email: string;
    name: string;
    phone: string;
    createdAt: string;
    zipCode: string;
    yearBirth: number;
    description: string;
  }
}

export interface State {
  list: BaseState<PaginatedResponse<Report>>,
  byId: BaseState<Report>,
  create: BaseState<ReportCreateData>,
  update: BaseState<ReportUpdateData>,
  delete: BaseState<{ id: string }>
}

const models = ['list', 'byId', 'create', 'update', 'delete'];

export const defaultState: State = createCRUDStates<State>(models);

export const report = createSlice({
  name: moduleName,
  initialState: defaultState,
  reducers: createCRUDModel(models, moduleName),
});

export const { actions } = report;

export function useReport(): State {
  return useSelector((state: RootState) => state[moduleName]);
}

export function* saga() {
  yield* listSaga();
  yield* byIdSaga();
  yield* createSaga();
  yield* updateSaga();
  yield* deleteSaga();
}
